import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  blogArticleSchema,
  type BlogArticleFormValues,
} from "@/lib/validation/article-schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { I18nInput } from "./i18n-input";
import { ImageUploadField } from "./image-upload-field";
import { SocialAccountsField } from "./social-accounts-field";
import { ArticleType } from "@/types/enums";

interface ArticleFormProps {
  defaultValues?: Partial<BlogArticleFormValues>;
  onSubmit: (data: BlogArticleFormValues) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export function ArticleForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel = "저장",
}: ArticleFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
  } = useForm<BlogArticleFormValues>({
    resolver: zodResolver(blogArticleSchema),
    defaultValues: {
      type: ArticleType.BLOG,
      title: { ko: "", en: null },
      excerpt: { ko: "", en: null },
      url: { ko: "", en: null },
      authorInfo: {
        name: { ko: "", en: null },
      },
      ...defaultValues,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>기본 정보</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <I18nInput
                label="제목"
                value={field.value}
                onChange={field.onChange}
                error={errors.title?.ko?.message}
                required
              />
            )}
          />

          <Controller
            name="excerpt"
            control={control}
            render={({ field }) => (
              <I18nInput
                label="요약"
                value={field.value}
                onChange={field.onChange}
                multiline
                rows={3}
                error={errors.excerpt?.ko?.message}
                required
              />
            )}
          />

          <Controller
            name="url"
            control={control}
            render={({ field }) => (
              <I18nInput
                label="블로그 URL"
                value={field.value}
                onChange={field.onChange}
                placeholder="https://example.com/article"
                error={errors.url?.ko?.message}
                required
              />
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>썸네일 (선택)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Controller
            name="thumbnail.url"
            control={control}
            render={({ field }) => (
              <ImageUploadField
                label="썸네일 이미지"
                value={field.value || { ko: "", en: null }}
                onChange={field.onChange}
                category="post-thumbnail"
              />
            )}
          />

          <Controller
            name="thumbnail.alt"
            control={control}
            render={({ field }) => (
              <I18nInput
                label="대체 텍스트"
                value={field.value || { ko: "", en: null }}
                onChange={field.onChange}
              />
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>작성자 정보</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Controller
            name="authorInfo.name"
            control={control}
            render={({ field }) => (
              <I18nInput
                label="작성자 이름"
                value={field.value}
                onChange={field.onChange}
                error={errors.authorInfo?.name?.ko?.message}
                required
              />
            )}
          />

          <Controller
            name="authorInfo.profileImageURL"
            control={control}
            render={({ field }) => (
              <ImageUploadField
                label="프로필 이미지 (선택)"
                value={field.value || { ko: "", en: null }}
                onChange={field.onChange}
                category="post-thumbnail"
              />
            )}
          />

          <Controller
            name="authorInfo.biography"
            control={control}
            render={({ field }) => (
              <I18nInput
                label="작성자 소개 (선택)"
                value={field.value || { ko: "", en: null }}
                onChange={field.onChange}
                multiline
                rows={3}
              />
            )}
          />

          <Separator />

          <SocialAccountsField
            control={control}
            name="authorInfo.socialAccounts"
          />
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {isSubmitted && Object.keys(errors).length > 0 && (
          <p className="text-destructive text-sm">
            입력 항목을 확인해주세요.
          </p>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          취소
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "저장 중..." : submitLabel}
        </Button>
      </div>
    </form>
  );
}
